import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

export type Theme = 'dark' | 'light';

const STORAGE_KEY = 'rp:theme';

/**
 * Thème initial : choix mémorisé (localStorage `rp:theme`), sinon
 * `prefers-color-scheme` pour le premier rendu, sinon sombre (spec 05 §1).
 */
function getInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'dark';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'dark' || stored === 'light') return stored;
  } catch {
    /* stockage indisponible (navigation privée…) → on suit le système */
  }
  if (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: light)').matches
  ) {
    return 'light';
  }
  return 'dark';
}

type ThemeContextValue = {
  theme: Theme;
  /** Bascule sombre ↔ clair et mémorise le choix. */
  toggle: () => void;
  setTheme: (theme: Theme) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

/** Pose `data-theme` sur `<html>` et persiste le choix utilisateur. */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(getInitialTheme);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;
  }, [theme]);

  // Handlers et valeur de contexte mémoïsés par le React Compiler (module compilé,
  // ADR 0010) — pure perf, jamais dans des deps d'effet.
  const setTheme = (next: Theme) => {
    setThemeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* pas bloquant : le thème vivra le temps de la session */
    }
  };

  const toggle = () => {
    setThemeState((prev) => {
      const next: Theme = prev === 'dark' ? 'light' : 'dark';
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* idem */
      }
      return next;
    });
  };

  const value = { theme, toggle, setTheme };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/** `const { theme, toggle } = useTheme()` — à brancher sur `<ThemeToggle>`. */
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme() doit être utilisé sous <ThemeProvider>');
  return ctx;
}
